import { useState, useRef } from "react";
import { Box, Flex, Text, Button, Input, Stack, HStack, Icon, Image, IconButton, Textarea } from "@chakra-ui/react";
import { Camera, X, ImagePlus } from "lucide-react";
import { CameraCapture } from "@/components/CameraCapture";
import { SignaturePad } from "@/components/SignaturePad";
import { WorkerPicker } from "@/components/WorkerPicker";
import { inputStyle } from "@/pages/auth/AuthShell";
import { colors } from "@/theme/colors";

export const PHOTO_MAX_MB = 10;
export const PHOTO_MAX_COUNT = 5;
const PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
const PHOTO_EXT_RE = /\.(jpe?g|png|webp|hei[cf])$/i;
const extToType = (name) => {
  const ext = (name?.match(/\.([a-z0-9]+)$/i)?.[1] || "").toLowerCase();
  return { jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", webp: "image/webp", heic: "image/heic", heif: "image/heif" }[ext];
};

const bigInput = { ...inputStyle, height: "48px", fontSize: "md", bg: colors.white };

// Big tap-friendly segmented choice (Pass/Fail/NA, Yes/No/NA)
const Segmented = ({ options, value, onChange, testId }) => (
  <Flex gap="2">
    {options.map((o) => {
      const active = value === o.value;
      const tone = o.tone || colors.teal;
      return (
        <Button key={o.value} flex="1" onClick={() => onChange(active ? null : o.value)}
          data-testid={`${testId}-${o.value}`}
          bg={active ? tone : colors.white} color={active ? "white" : colors.textPrimary}
          border="1.5px solid" borderColor={active ? tone : colors.border}
          _hover={{ borderColor: tone }}>
          {o.label}
        </Button>
      );
    })}
  </Flex>
);

const OptionRow = ({ checked, multi, onClick, children, testId }) => (
  <Flex as="button" type="button" onClick={onClick} data-testid={testId} w="full" align="center" gap="3"
    px="3.5" py="3" borderRadius="10px" border="1.5px solid" textAlign="left"
    borderColor={checked ? colors.teal : colors.border} bg={checked ? colors.tealTint : colors.white}>
    <Box w="18px" h="18px" flexShrink={0} borderRadius={multi ? "4px" : "full"} border="2px solid"
      borderColor={checked ? colors.teal : colors.border} bg={checked ? colors.teal : "transparent"}
      display="flex" alignItems="center" justifyContent="center">
      {checked && <Box w="7px" h="7px" borderRadius={multi ? "1px" : "full"} bg="white" />}
    </Box>
    <Text fontSize="sm" color={colors.textPrimary}>{children}</Text>
  </Flex>
);

// Validates + reads picked files into dataURL photo objects. Returns the new
// photo list and the last validation error (if any).
const readPhotoFiles = async (files, existing) => {
  const next = [...existing];
  let err = null;
  for (const f of files) {
    if (next.length >= PHOTO_MAX_COUNT) { err = `Maximum ${PHOTO_MAX_COUNT} photos per question.`; break; }
    // Camera captures sometimes arrive with an empty MIME type — fall back to the extension.
    const okType = PHOTO_TYPES.includes(f.type) || (!f.type && PHOTO_EXT_RE.test(f.name)) || /\.hei[cf]$/i.test(f.name);
    if (!okType) { err = `"${f.name}" isn't a supported photo. Please use JPG, PNG, WEBP or HEIC.`; continue; }
    if (f.size > PHOTO_MAX_MB * 1024 * 1024) { err = `"${f.name}" is ${(f.size / 1024 / 1024).toFixed(1)} MB — photos must be under ${PHOTO_MAX_MB} MB.`; continue; }
    const dataUrl = await new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    });
    next.push({ name: f.name || "photo.jpg", type: f.type || extToType(f.name) || "image/jpeg", size: f.size, dataUrl });
  }
  return { next, err };
};

const PhotoThumbs = ({ photos, fieldId, onRemove }) => (
  <Flex gap="2" flexWrap="wrap" mb="2">
    {photos.map((p, i) => (
      <Box key={p.path || p.id || i} position="relative">
        {(p.dataUrl || p.signedUrl) ? (
          <Image src={p.dataUrl || p.signedUrl} alt={p.name} boxSize="72px" objectFit="cover" borderRadius="8px" border="1px solid" borderColor={colors.border} />
        ) : (
          <Flex boxSize="72px" align="center" justify="center" borderRadius="8px" bg={colors.cream}><Icon as={Camera} /></Flex>
        )}
        <IconButton aria-label="Remove photo" size="2xs" position="absolute" top="-6px" right="-6px"
          borderRadius="full" bg={colors.poppy} color="white"
          onClick={() => onRemove(i)} data-testid={`photo-remove-${fieldId}-${i}`}>
          <X size={11} />
        </IconButton>
      </Box>
    ))}
  </Flex>
);

function PhotoField({ field, value, onChange }) {
  const [err, setErr] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const fallbackRef = useRef(null);
  const photos = Array.isArray(value) ? value : [];
  const full = photos.length >= PHOTO_MAX_COUNT;

  const addCapturedShot = (dataUrl) => {
    setShowCamera(false);
    if (!dataUrl) return;
    const size = Math.round((dataUrl.length * 3) / 4);
    onChange([...photos, { name: `camera-${Date.now()}.jpg`, type: "image/jpeg", size, dataUrl }]);
  };

  const openCamera = () => {
    setErr(null);
    if (navigator.mediaDevices?.getUserMedia) setShowCamera(true);
    else fallbackRef.current?.click(); // very old browsers: native capture input
  };

  const closeCamera = (reason) => {
    setShowCamera(false);
    if (reason === "fallback") fallbackRef.current?.click();
  };

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const { next, err: readErr } = await readPhotoFiles(files, photos);
    setErr(readErr);
    onChange(next);
  };

  return (
    <Box>
      {photos.length > 0 && (
        <PhotoThumbs photos={photos} fieldId={field.id} onRemove={(i) => onChange(photos.filter((_, j) => j !== i))} />
      )}
      {showCamera && <CameraCapture onCapture={addCapturedShot} onClose={closeCamera} />}
      {/* Hidden native capture input — fallback when in-app camera can't start */}
      <input type="file" accept="image/*" capture="environment" ref={fallbackRef}
        onChange={pick} data-testid={`photo-input-camera-${field.id}`}
        style={{ position: "absolute", width: "1px", height: "1px", opacity: 0, overflow: "hidden" }} />
      <Flex gap="2">
        <Box as="button" type="button" onClick={full ? undefined : openCamera} disabled={full}
          data-testid={`photo-camera-btn-${field.id}`} flex="1" display="flex"
          alignItems="center" justifyContent="center" gap="2" h="52px" borderRadius="10px"
          border="1.5px dashed" borderColor={colors.border} color={colors.textSecondary}
          fontSize="sm" fontWeight="600" bg={colors.white}
          cursor={full ? "not-allowed" : "pointer"} opacity={full ? 0.55 : 1}
          _hover={full ? {} : { borderColor: colors.teal, color: colors.tealHover }}>
          <Icon as={Camera} boxSize="4" />
          Take photo
        </Box>
        <Box as="label" data-testid={`photo-btn-${field.id}`} position="relative" flex="1" display="flex"
          alignItems="center" justifyContent="center" gap="2" h="52px" borderRadius="10px"
          border="1.5px dashed" borderColor={colors.border} color={colors.textSecondary}
          fontSize="sm" fontWeight="600" bg={colors.white}
          cursor={full ? "not-allowed" : "pointer"} opacity={full ? 0.55 : 1}
          _hover={full ? {} : { borderColor: colors.teal, color: colors.tealHover }}>
          <Icon as={ImagePlus} boxSize="4" />
          Choose photo
          {!full && (
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" multiple
              onChange={pick} data-testid={`photo-input-${field.id}`}
              style={{ position: "absolute", width: "1px", height: "1px", opacity: 0, overflow: "hidden" }} />
          )}
        </Box>
      </Flex>
      <Text fontSize="xs" color={colors.textSecondary} mt="1">JPG, PNG, WEBP or HEIC · max {PHOTO_MAX_MB} MB each</Text>
      {err && <Text fontSize="sm" color={colors.criticalText} mt="1" data-testid={`photo-error-${field.id}`}>{err}</Text>}
    </Box>
  );
}

const OTHER = "__other__";

// Multi-select answers arrive as {picked:[...]} (new) or a bare array (legacy).
const pickedItems = (value) => {
  if (Array.isArray(value?.picked)) return value.picked;
  if (Array.isArray(value)) return value;
  return [];
};

function DropdownOtherOption({ field, multi, selected, isOther, otherText, value, onChange, toggleMulti }) {
  const otherActive = multi ? selected.includes(OTHER) : isOther;
  return (
    <>
      <OptionRow multi={multi} testId={`opt-${field.id}-other`} checked={otherActive}
        onClick={() => (multi ? toggleMulti(OTHER) : onChange(isOther ? null : { other: "" }))}>
        Other…
      </OptionRow>
      {otherActive && (
        <Input {...bigInput} placeholder="Please specify" data-testid={`other-input-${field.id}`}
          value={multi ? otherText : value?.other || ""}
          onChange={(e) => (multi ? onChange({ picked: selected, other: e.target.value }) : onChange({ other: e.target.value }))} />
      )}
    </>
  );
}

function DropdownField({ field, list, multi, value, onChange }) {
  if (!list) return <Text fontSize="sm" color={colors.textSecondary}>This question's list is unavailable.</Text>;
  const selected = multi ? pickedItems(value) : value;
  const otherText = multi ? value?.other || "" : "";
  const isOther = multi ? selected.includes(OTHER) : typeof value === "object" && value?.other !== undefined;

  const toggleMulti = (item) => {
    const next = selected.includes(item) ? selected.filter((x) => x !== item) : [...selected, item];
    onChange({ picked: next, ...(next.includes(OTHER) ? { other: otherText } : {}) });
  };

  return (
    <Stack gap="2">
      {list.items.map((item, i) => (
        <OptionRow key={item} multi={multi} testId={`opt-${field.id}-${i}`}
          checked={multi ? selected.includes(item) : selected === item}
          onClick={() => (multi ? toggleMulti(item) : onChange(selected === item ? null : item))}>
          {item}
        </OptionRow>
      ))}
      {list.include_other && (
        <DropdownOtherOption field={field} multi={multi} selected={selected} isOther={isOther}
          otherText={otherText} value={value} onChange={onChange} toggleMulti={toggleMulti} />
      )}
    </Stack>
  );
}

function WorkerField({ field, workers, multi, value, onChange }) {
  if (!workers?.length) return <Text fontSize="sm" color={colors.textSecondary}>No workers found for your company.</Text>;
  // Saved shape is unchanged: single = {id, name} | null, multi = [{id, name}].
  const selected = multi ? (Array.isArray(value) ? value : []) : (value?.id ? [value] : []);
  const fallbackNames = Object.fromEntries(selected.map((s) => [s.id, s.name]));
  const handle = (ids) => {
    const objs = ids.map((id) => {
      const w = workers.find((x) => x.id === id);
      return { id, name: w ? w.full_name : fallbackNames[id] || "" };
    });
    onChange(multi ? objs : objs[0] || null);
  };
  return (
    <WorkerPicker workers={workers} multi={multi} value={selected.map((s) => s.id)} onChange={handle}
      fallbackNames={fallbackNames} testId={`worker-${field.id}`}
      placeholder={multi ? "Select workers" : "Select worker"} />
  );
}

// Static option sets for segmented (tap-choice) field types.
const SEG_OPTIONS = {
  pass_fail_na: [
    { value: "pass", label: "Pass", tone: colors.sageDeep },
    { value: "fail", label: "Fail", tone: colors.poppy },
    { value: "na", label: "N/A", tone: colors.textSecondary },
  ],
  yes_no_na: [
    { value: "yes", label: "Yes", tone: colors.sageDeep },
    { value: "no", label: "No", tone: colors.poppy },
    { value: "na", label: "N/A", tone: colors.textSecondary },
  ],
  yes_no: [ // legacy
    { value: "yes", label: "Yes", tone: colors.sageDeep },
    { value: "no", label: "No", tone: colors.poppy },
  ],
};

// Native input attributes for simple text-like field types.
const TEXT_INPUT_ATTRS = {
  number: { type: "number", inputMode: "decimal" },
  date: { type: "date" },
  time: { type: "time" },
};

export function FieldInput({ field, value, onChange, listsById, workers }) {
  if (SEG_OPTIONS[field.type]) {
    return <Segmented value={value} onChange={onChange} testId={`seg-${field.id}`} options={SEG_OPTIONS[field.type]} />;
  }
  switch (field.type) {
    case "info":
      return (
        <Box px="3.5" py="3" borderRadius="10px" bg={colors.tealTint} borderLeft="3px solid" borderColor={colors.teal}>
          <Text fontSize="sm" color={colors.textPrimary}>{field.label}</Text>
        </Box>
      );
    case "checkbox":
      return (
        <OptionRow multi checked={!!value} onClick={() => onChange(!value)} testId={`check-${field.id}`}>
          {value ? "Confirmed" : "Tap to confirm"}
        </OptionRow>
      );
    case "dropdown_one":
      return <DropdownField field={field} list={listsById[field.list_id]} value={value} onChange={onChange} />;
    case "dropdown_multi":
      return <DropdownField field={field} list={listsById[field.list_id]} multi value={value} onChange={onChange} />;
    case "multiple_choice": // legacy inline options
      return (
        <Stack gap="2">
          {(field.options || []).map((o, i) => (
            <OptionRow key={o} checked={value === o} onClick={() => onChange(value === o ? null : o)} testId={`opt-${field.id}-${i}`}>{o}</OptionRow>
          ))}
        </Stack>
      );
    case "select_worker":
      return <WorkerField field={field} workers={workers} value={value} onChange={onChange} />;
    case "select_workers":
      return <WorkerField field={field} workers={workers} multi value={value} onChange={onChange} />;
    case "long_text":
      return <Textarea {...inputStyle} height="auto" minH="110px" py="3" bg={colors.white} fontSize="md"
        value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} />;
    case "photo":
      return <PhotoField field={field} value={value} onChange={onChange} />;
    case "signature":
      return <SignaturePad value={value} onChange={onChange} testId={`signature-${field.id}`} />;
    default: // short_text, number, date, time
      return <Input {...bigInput} {...(TEXT_INPUT_ATTRS[field.type] || {})}
        value={field.type === "number" ? value ?? "" : value || ""}
        onChange={(e) => onChange(e.target.value)} data-testid={`input-${field.id}`} />;
  }
}

// Is a required field answered?
export const isAnswered = (field, value) => {
  switch (field.type) {
    case "info": return true;
    case "checkbox": return value === true;
    case "photo": return Array.isArray(value) && value.length > 0;
    case "signature": return typeof value === "string" && value.startsWith("data:image");
    case "dropdown_multi": {
      const picked = pickedItems(value);
      if (picked.includes(OTHER)) return (value?.other || "").trim().length > 0;
      return picked.length > 0;
    }
    case "dropdown_one":
      if (value && typeof value === "object") return (value.other || "").trim().length > 0;
      return !!value;
    case "select_worker": return !!value?.id;
    case "select_workers": return Array.isArray(value) && value.length > 0;
    default:
      return value !== null && value !== undefined && String(value).trim() !== "";
  }
};

// Human-readable answer for the review screen.
export const displayAnswer = (field, value) => {
  if (value === null || value === undefined || value === "") return "—";
  switch (field.type) {
    case "checkbox": return value ? "Confirmed" : "—";
    case "photo": return `${value.length} photo${value.length === 1 ? "" : "s"}`;
    case "signature": return "Signed";
    case "select_worker": return value?.name || "—";
    case "select_workers": return value.map((v) => v.name).join(", ") || "—";
    case "dropdown_multi": {
      const picked = pickedItems(value);
      return picked.map((p) => (p === OTHER ? `Other: ${value?.other || ""}` : p)).join(", ") || "—";
    }
    case "dropdown_one":
      if (typeof value === "object") return `Other: ${value.other || ""}`;
      return value;
    case "pass_fail_na": return { pass: "Pass", fail: "Fail", na: "N/A" }[value] || value;
    case "yes_no_na": case "yes_no": return { yes: "Yes", no: "No", na: "N/A" }[value] || value;
    default: return String(value);
  }
};
